import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../i18n/format";
import { imageSrc } from "@shared/vod-images";
import type { GuideEvent, GuideEventDetail } from "@shared/epg-guide";
import { foyerApi } from "../../http/foyer";
import { epgErrorText } from "./findings";
import { clock, day, span } from "./scheduleFormat";
import { tokens } from "../../theme";

/**
 * One EPG event's artwork and metadata - what `ChannelSchedulePreview`'s row
 * deliberately does not carry (see `@shared/epg-guide`'s header on
 * `toGuideEvent`: forwarding it on every one of ~250 events in a guide would
 * answer a schedule read with megabytes of catalogue). Opened for exactly the
 * one event an operator clicked, it costs one extra read.
 *
 * Read-only, on purpose, unlike `AssetEditorDialog`. Nobody authors EPG - the
 * programme asset behind this event is ingest-owned and machine-minted per
 * airing (see `epg-mapping`'s header), so an editor here would invite editing
 * a row the next ingest run silently supersedes or the purge reclaims inside
 * ~9 days. This is diagnosis, the same job the schedule panel itself has: does
 * this event actually carry a picture and a description, or is the catalogue
 * as thin as the guide row looked.
 */
export interface EpgEventDetailDialogProps {
  channelAssetId: string;
  /**
   * What the schedule row already showed, drawn immediately so the dialog is
   * never a blank spinner over nothing the operator hasn't already seen.
   */
  fallback: Pick<
    GuideEvent,
    | "epgEventId"
    | "title"
    | "titleLanguage"
    | "synopsis"
    | "startScheduled"
    | "endScheduled"
    | "image"
  >;
  /** The language the schedule panel is reading in (`data.language`), sent so
   *  the detail read resolves to the SAME row the guide row did whenever one
   *  exists, rather than a second, independent (and possibly different)
   *  language fallback. */
  language: string;
  onClose: () => void;
}

/** Poster-shaped art first, decoration (`logo`, `other`) last - the hero image
 *  should be the one an operator would recognise the programme by. */
const IMAGE_PRIORITY: Record<
  GuideEventDetail["images"][number]["type"],
  number
> = {
  poster: 0,
  cover: 1,
  banner: 2,
  thumbnail: 3,
  other: 4,
  logo: 5,
};

export default function EpgEventDetailDialog({
  channelAssetId,
  fallback,
  language,
  onClose,
}: EpgEventDetailDialogProps) {
  const { t } = useTranslation("channels");
  const fmt = useFormat();
  const [detail, setDetail] = useState<GuideEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    foyerApi
      .getEpgEventDetail(channelAssetId, fallback.epgEventId, language)
      .then((answer) => {
        if (!cancelled) setDetail(answer.event);
      })
      .catch(
        (e: { code?: string; message: string; body?: { detail?: string } }) => {
          if (!cancelled) {
            setError(
              epgErrorText(t, {
                code: e.code,
                detail: e.body?.detail,
                message: e.message,
              })
            );
          }
        }
      )
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `fallback.epgEventId` is the only field of `fallback` that identifies
    // WHICH read this is; the rest is display-only and re-running the fetch
    // when a formatted title changes would be pointless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelAssetId, fallback.epgEventId, language, t]);

  const images = [...(detail?.images ?? [])].sort(
    (a, b) => IMAGE_PRIORITY[a.type] - IMAGE_PRIORITY[b.type]
  );
  const hero = images[0] ?? fallback.image;
  const heroSrc = hero
    ? imageSrc(hero, { width: 640, height: 360, fit: "cover" })
    : null;

  const title =
    detail?.title ||
    fallback.title ||
    t("epgEventDetailDialog.untitledProgramme");
  const synopsis = detail?.synopsisLong || fallback.synopsis;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="epg-event-detail-dialog"
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={2}
        sx={{ p: 3, pb: 2 }}
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Typography
            sx={{
              typography: "titleLg",
              fontWeight: "fontWeightSemibold",
              color: tokens.ink,
            }}
            data-testid="epg-detail-title"
          >
            {title}
          </Typography>
          <Typography
            variant="bodySm"
            sx={{ color: tokens.inkMuted, mt: 0.25 }}
          >
            {t("epgEventDetailDialog.timeline", {
              day: day(fmt, fallback.startScheduled),
              start: clock(fmt, fallback.startScheduled),
              end: clock(fmt, fallback.endScheduled),
              span: span(
                t,
                Math.round(
                  (Date.parse(fallback.endScheduled) -
                    Date.parse(fallback.startScheduled)) /
                    1000
                )
              ),
            })}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label={t("epgEventDetailDialog.close")}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" data-testid="epg-detail-error">
            {error}
          </Alert>
        )}

        {loading && !detail && !error && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {!error && (detail || fallback.image) && (
          <Stack spacing={2}>
            {heroSrc && (
              <Box
                component="img"
                src={heroSrc}
                alt=""
                data-testid="epg-detail-artwork"
                sx={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  objectFit: "cover",
                  borderRadius: 2,
                  bgcolor: tokens.divider,
                }}
              />
            )}
            {!heroSrc && !loading && (
              <Box
                data-testid="epg-detail-no-artwork"
                sx={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  borderRadius: 2,
                  bgcolor: tokens.divider,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                  {t("epgEventDetailDialog.noArtwork")}
                </Typography>
              </Box>
            )}

            {images.length > 1 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {images.map((image, index) => {
                  const src = imageSrc(image, {
                    width: 96,
                    height: 54,
                    fit: "cover",
                  });
                  if (!src) return null;
                  return (
                    <Box
                      key={`${image.url}-${index}`}
                      component="img"
                      src={src}
                      alt=""
                      title={`${image.type} · ${image.language}`}
                      sx={{
                        width: 96,
                        height: 54,
                        objectFit: "cover",
                        borderRadius: 1,
                        border: `1px solid ${tokens.divider}`,
                      }}
                    />
                  );
                })}
              </Stack>
            )}

            {synopsis && (
              <Typography variant="body2" sx={{ color: tokens.ink }}>
                {synopsis}
              </Typography>
            )}

            {detail && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {detail.genres.map((genre) => (
                  <Chip key={genre} label={genre} size="small" />
                ))}
                {detail.parentalRatings.map((rating) => (
                  <Chip
                    key={`${rating.country}-${rating.ratingValue}`}
                    label={`${rating.country.toUpperCase()} ${rating.ratingValue}+`}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {detail.seasonNumber != null &&
                  detail.episodeNumber != null && (
                    <Chip
                      label={`S${detail.seasonNumber} E${detail.episodeNumber}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                {detail.productionYear ? (
                  <Chip
                    label={String(detail.productionYear)}
                    size="small"
                    variant="outlined"
                  />
                ) : null}
                {detail.countryOfOrigin && (
                  <Chip
                    label={detail.countryOfOrigin.toUpperCase()}
                    size="small"
                    variant="outlined"
                  />
                )}
                {detail.provider && (
                  <Chip
                    label={detail.provider}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            )}

            {detail?.titleLanguage && detail.titleLanguage !== language && (
              <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
                {t("epgEventDetailDialog.shownIn", {
                  shown: detail.titleLanguage.toUpperCase(),
                  wanted: language.toUpperCase(),
                })}
              </Typography>
            )}

            <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
              {t("epgEventDetailDialog.readOnly")}
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
